import type { Readable } from 'node:stream';

import type { ObjectId } from 'mongodb';

import type { Meta } from './Entities';
import type { CreatePriceSheetMapping, PriceSheetMapping } from './PriceSheetMapping';
import type { PpaPricePerWattOption } from './ProductPricing';
import { ProgramType } from './ProgramTypes';

export enum PriceSheetStatus {
  active = 'active',
  archived = 'archived',
}

export interface BasePriceSheet {
  id: ObjectId;
  name: string;
  description?: string;
  status: PriceSheetStatus;
  escalationRates: Array<number>;
  createdDate: Date;
  archivedDate?: Date;
  meta?: Meta;
  programType: ProgramType;
}

export interface SolarPriceSheetDocument extends BasePriceSheet {
  kwhRateFloor: number;
  kwhRateCeiling: number;
  productionFactorFloor: number;
  productionFactorCeiling: number;
}

export interface DoePrPriceSheetDocument extends BasePriceSheet {
  minSystemSize: number;
  maxSystemSize: number;
  minBatteryCapacity: number;
  maxBatteryCapacity: number;
}

export interface ConsumerSolarPriceSheetDocument extends BasePriceSheet {
  minSystemSize: number;
  maxSystemSize: number;
  minProductionFactor: number;
  maxProductionFactor: number;
}

export interface HVACPriceSheetDocument extends BasePriceSheet {
  terms: Array<number>;
  systemCosts: Array<number>;
}

export interface EnergyBackupPriceSheetDocument extends BasePriceSheet {
  terms: Array<number>;
  systemCosts: Array<number>;
}

export interface WaterHeaterPriceSheetDocument extends BasePriceSheet {
  terms: Array<number>;
  systemCosts: Array<number>;
}

// export type PriceSheetDocument = SolarPriceSheetDocument | DoePrPriceSheetDocument;
export type PriceSheetDocument<T extends ProgramType> = T extends 'solar'
  ? SolarPriceSheetDocument
  : T extends 'doePr'
    ? DoePrPriceSheetDocument
    : T extends 'consumerSolar'
      ? ConsumerSolarPriceSheetDocument
      : T extends 'hvac'
        ? HVACPriceSheetDocument
        : T extends 'energyBackup'
          ? EnergyBackupPriceSheetDocument
          : T extends 'waterHeater'
            ? WaterHeaterPriceSheetDocument
            : SolarPriceSheetDocument;

export interface SolarPriceEntry {
  escalationRate: number;
  kwhRate: number;
  ppwRateOptions: Array<PpaPricePerWattOption>;
}

export interface DoePrPricePerWattOption {
  ppwRate: number;
  monthlyPayment: number;
  minSystemSize: number;
  maxSystemSize: number;
  minBatteryCapacity: number;
  maxBatteryCapacity: number;
}

export interface DoePrPriceEntry extends DoePrPricePerWattOption {
  escalationRate: number;
}

export interface ConsumerSolarPriceEntry {
  escalationRate: number;
  minSystemSize: number;
  maxSystemSize: number;
  minProductionFactor: number;
  maxProductionFactor: number;
  kwhRate: number;
  ppwRate: number;
}

export interface HVACPriceEntry {
  escalationRate: number;
  term: number;
  paymentFactor: number;
  minSystemCost: number;
  maxSystemCost: number;
}

export interface EnergyBackupPriceEntry {
  escalationRate: number;
  term: number;
  paymentFactor: number;
  minSystemCost: number;
  maxSystemCost: number;
}

export interface WaterHeaterPriceEntry {
  escalationRate: number;
  term: number;
  paymentFactor: number;
  minSystemCost: number;
  maxSystemCost: number;
}

export interface PriceSheetDetails<T extends ProgramType> {
  id: ObjectId;
  name: string;
  prices: Array<
    T extends 'solar'
      ? SolarPriceEntry
      : T extends 'doePr'
        ? DoePrPriceEntry
        : T extends 'consumerSolar'
          ? ConsumerSolarPriceEntry
          : T extends 'hvac'
            ? HVACPriceEntry
            : T extends 'energyBackup'
              ? EnergyBackupPriceEntry
              : T extends 'waterHeater'
                ? WaterHeaterPriceEntry
                : SolarPriceEntry
  >;
}

export interface PriceSheetService<T extends ProgramType> {
  programType: ProgramType;
  createPriceSheetMapping(data: CreatePriceSheetMapping<T>): Promise<PriceSheetMapping<T>[]>;
  importPriceSheet(priceSheetName: string, priceSheetContents: Readable): Promise<PriceSheetDocument<T>>;
}
